import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { SeasonService } from '../../services/season.service';
import { TeamContextService } from '../../services/team-context.service';
import { ApiService } from '../../services/api.service';
import { PosthogService } from '../../services/posthog.service';
import {
  SessionResult,
  Season,
  StaticWeatherConfig,
  StaticWeatherSkies,
  StaticWeatherTrackMoisture,
  StaticWeatherWindDirection,
  Team,
} from '@spectrace/shared';

const DEFAULT_STATIC_WEATHER_CONFIG: StaticWeatherConfig = {
  skies: 'partly_cloudy',
  trackMoisture: 'none',
  airTempC: 25.5,
  relativeHumidityPct: 55,
  windSpeedMps: 2,
  windDirection: 'north',
};

@Component({
  selector: 'app-session-starter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './session-starter.component.html',
  styleUrls: ['./session-starter.component.css'],
})
export class SessionStarterComponent implements OnInit {
  @Input() forceCreateMode = false;
  @Output() sessionStarted = new EventEmitter<string>();

  sessionName = '';
  visibility: 'team' | 'public' = 'team';
  hasTeamContext = false;
  selectedTrackId: number | null = null; // Selected track ID
  enableScheduleWindow = false;
  plannedStartTime = ''; // datetime-local value
  plannedEndTime = ''; // datetime-local value
  lapsPerDriver = 10; // Default laps per driver
  allowSetupChanges = true;
  enforceStaticWeather = false;
  staticWeatherSkies: StaticWeatherSkies = 'clear';
  staticTrackMoisture: StaticWeatherTrackMoisture = 'none';
  staticAirTempC?: number;
  staticRelativeHumidityPct?: number;
  staticWindSpeedMps?: number;
  staticWindDirection: StaticWeatherWindDirection = 'north';
  restrictByIRating = false;
  minIRating?: number;
  maxIRating?: number;
  activeSession: SessionResult | null = null;
  sessionReady = false; // New state: players added but session not created
  error = '';

  // Session sharing
  participants: any[] = [];
  copySuccess = false;

  // Season selection
  seasons: Season[] = [];
  selectedSeasonId: string | undefined = undefined;

  // Track selection with typeahead
  tracks: any[] = [];
  filteredTracks: any[] = [];
  trackSearchQuery = '';
  showTrackDropdown = false;

  // Car and car class selection (multiselect)
  availableCars: any[] = [];
  availableCarClasses: any[] = [];
  selectedCarClasses: string[] = [];
  carSearchQuery = '';
  filteredCars: any[] = [];
  showCarDropdown = false;

  // Available laps per driver options (5-50 laps)
  readonly lapsPerDriverOptions = [5, 10, 15, 20, 25, 30, 40, 50];
  readonly staticWeatherSkyOptions: Array<{
    value: StaticWeatherSkies;
    label: string;
  }> = [
    { value: 'clear', label: 'Clear' },
    { value: 'partly_cloudy', label: 'Partly Cloudy' },
    { value: 'mostly_cloudy', label: 'Mostly Cloudy' },
    { value: 'overcast', label: 'Overcast' },
  ];
  readonly staticTrackMoistureOptions: Array<{
    value: StaticWeatherTrackMoisture;
    label: string;
  }> = [
    { value: 'none', label: 'None' },
    { value: 'very_light', label: 'Very Light' },
    { value: 'light', label: 'Light' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'heavy', label: 'Heavy' },
    { value: 'very_heavy', label: 'Very Heavy' },
  ];
  readonly staticWindDirectionOptions: Array<{
    value: StaticWeatherWindDirection;
    label: string;
  }> = [
    { value: 'north', label: 'North' },
    { value: 'northeast', label: 'Northeast' },
    { value: 'east', label: 'East' },
    { value: 'southeast', label: 'Southeast' },
    { value: 'south', label: 'South' },
    { value: 'southwest', label: 'Southwest' },
    { value: 'west', label: 'West' },
    { value: 'northwest', label: 'Northwest' },
  ];

  constructor(
    private sessionService: SessionService,
    private seasonService: SeasonService,
    private teamContextService: TeamContextService,
    private apiService: ApiService,
    private posthogService: PosthogService,
    private router: Router,
  ) {
    this.sessionService.activeSession$.subscribe((session) => {
      this.activeSession = session;
      if (!session) {
        this.sessionReady = false;
        this.sessionName = '';
        this.visibility = this.getDefaultVisibility();
        this.enableScheduleWindow = false;
        this.plannedStartTime = '';
        this.plannedEndTime = '';
        this.lapsPerDriver = 10; // Reset to default
        this.allowSetupChanges = true;
        this.resetStaticWeatherConfig();
        this.restrictByIRating = false;
        this.minIRating = undefined;
        this.maxIRating = undefined;
        this.participants = [];
      } else {
        // Load participants when session becomes active
        this.loadParticipants();
      }
    });

    this.teamContextService.currentTeam$.subscribe((team: Team | null) => {
      const hadTeamContext = this.hasTeamContext;
      this.hasTeamContext = !!team;
      if (!this.hasTeamContext) {
        this.visibility = 'public';
        this.selectedSeasonId = undefined;
        this.seasons = [];
      } else if (!hadTeamContext) {
        this.loadSeasons();
        this.visibility = this.getDefaultVisibility();
      }
    });
  }

  ngOnInit(): void {
    this.hasTeamContext = !!this.teamContextService.getCurrentTeamId();
    this.visibility = this.getDefaultVisibility();
    this.loadSeasons();
    this.loadTracks();
    this.loadCarClasses();
    this.loadCars();
  }

  loadSeasons(): void {
    if (!this.hasTeamContext) {
      this.seasons = [];
      this.selectedSeasonId = undefined;
      return;
    }

    const teamId = this.teamContextService.getCurrentTeamId();
    if (teamId) {
      this.seasonService.getSeasonsByTeam(teamId).subscribe((seasons) => {
        this.seasons = seasons;
      });
    }
  }

  loadTracks(): void {
    this.apiService.getTracks().subscribe({
      next: (response) => {
        this.tracks = response.tracks;
        this.filteredTracks = response.tracks;
      },
      error: (error) => {
        console.error('Failed to load tracks:', error);
      },
    });
  }

  loadCarClasses(): void {
    this.apiService.getCarClasses().subscribe({
      next: (response) => {
        this.availableCarClasses = response.carClasses;
      },
      error: (error) => {
        console.error('Failed to load car classes:', error);
      },
    });
  }

  loadCars(): void {
    this.apiService.getCars().subscribe({
      next: (response) => {
        const sortedCars = [...response.cars].sort((a, b) =>
          this.getCarDisplayName(a).localeCompare(
            this.getCarDisplayName(b),
            undefined,
            {
              sensitivity: 'base',
            },
          ),
        );
        this.availableCars = sortedCars;
        this.filteredCars = sortedCars;
      },
      error: (error) => {
        console.error('Failed to load cars:', error);
      },
    });
  }

  filterTracks(): void {
    const query = this.trackSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredTracks = this.tracks;
      return;
    }
    this.filteredTracks = this.tracks.filter(
      (track) =>
        track.name.toLowerCase().includes(query) ||
        (track.configName && track.configName.toLowerCase().includes(query)),
    );
  }

  filterCars(): void {
    const query = this.carSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredCars = this.availableCars;
      return;
    }
    this.filteredCars = this.availableCars.filter(
      (car) =>
        car.name.toLowerCase().includes(query) ||
        (car.carName && car.carName.toLowerCase().includes(query)) ||
        car.carClasses.some(
          (cc: any) =>
            cc.carClass.name.toLowerCase().includes(query) ||
            (cc.carClass.shortName &&
              cc.carClass.shortName.toLowerCase().includes(query)),
        ),
    );
  }

  selectTrack(track: any): void {
    this.selectedTrackId = track.id;
    this.trackSearchQuery = this.getTrackDisplay(track);
    this.showTrackDropdown = false;
  }

  onTrackInputFocus(): void {
    this.showTrackDropdown = true;
    this.filterTracks();
  }

  onTrackInputBlur(): void {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      this.showTrackDropdown = false;
    }, 200);
  }

  onCarInputFocus(): void {
    this.showCarDropdown = true;
    this.filterCars();
  }

  onCarInputBlur(): void {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      this.showCarDropdown = false;
    }, 200);
  }

  getTrackDisplay(track: any): string {
    return track.configName
      ? `${track.name} - ${track.configName}`
      : track.name;
  }

  private getCarDisplayName(car: any): string {
    return (car.carName || car.name || '').toString();
  }

  getCarOptionValue(car: any): string {
    return this.getCarDisplayName(car);
  }

  getSelectedTrackName(): string {
    if (!this.selectedTrackId) return '';
    const track = this.tracks.find((t) => t.id === this.selectedTrackId);
    return track ? this.getTrackDisplay(track) : '';
  }

  getSeasonName(seasonId: string): string {
    const season = this.seasons.find((s) => s.id === seasonId);
    return season ? season.name : 'Unknown Season';
  }

  toggleCarClass(carClassName: string): void {
    const normalizedCarName = carClassName.trim();
    if (!normalizedCarName) {
      return;
    }

    const index = this.selectedCarClasses.indexOf(normalizedCarName);
    if (index >= 0) {
      this.selectedCarClasses.splice(index, 1);
    } else {
      this.selectedCarClasses.push(normalizedCarName);
    }

    this.showCarDropdown = true;
    this.filterCars();
  }

  isCarClassSelected(carClassName: string): boolean {
    return this.selectedCarClasses.includes(carClassName.trim());
  }

  prepareSession(): void {
    if (!this.sessionName.trim()) {
      this.error = 'Please enter a session name';
      this.posthogService.capture('session_prepare_failed', {
        reason: 'missing_session_name',
      });
      return;
    }

    if (!this.selectedTrackId) {
      this.error = 'Please select a track';
      this.posthogService.capture('session_prepare_failed', {
        reason: 'missing_track',
      });
      return;
    }

    if (
      this.enableScheduleWindow &&
      this.plannedStartTime &&
      this.plannedEndTime
    ) {
      const start = new Date(this.plannedStartTime);
      const end = new Date(this.plannedEndTime);
      if (end <= start) {
        this.error = 'End date must be after start date';
        this.posthogService.capture('session_prepare_failed', {
          reason: 'invalid_schedule_window',
        });
        return;
      }
    }

    if (
      this.enforceStaticWeather &&
      !this.buildStaticWeatherConfig()
    ) {
      this.error = 'Complete all static weather fields before continuing';
      this.posthogService.capture('session_prepare_failed', {
        reason: 'invalid_static_weather',
      });
      return;
    }

    if (
      this.restrictByIRating &&
      this.minIRating !== undefined &&
      this.maxIRating !== undefined &&
      this.minIRating > this.maxIRating
    ) {
      this.error =
        'Minimum iRating must be less than or equal to maximum iRating';
      this.posthogService.capture('session_prepare_failed', {
        reason: 'invalid_irating_range',
      });
      return;
    }

    this.error = '';
    this.posthogService.capture('session_prepare_succeeded', {
      visibility: this.visibility,
      hasScheduleWindow: this.enableScheduleWindow,
      hasIRatingRestriction: this.restrictByIRating,
      allowSetupChanges: this.allowSetupChanges,
      enforceStaticWeather: this.enforceStaticWeather,
      selectedCarClassCount: this.selectedCarClasses.length,
      hasSeasonSelection: !!this.selectedSeasonId,
    });
    this.sessionReady = true;
  }

  createSession(): void {
    if (!this.hasTeamContext && this.visibility !== 'public') {
      this.visibility = 'public';
    }

    const trimmedSessionName = this.sessionName.trim();

    if (!trimmedSessionName) {
      this.error = 'Please enter a session name';
      this.posthogService.capture('session_create_failed', {
        reason: 'missing_session_name',
      });
      return;
    }

    if (!this.selectedTrackId) {
      this.error = 'Please select a track';
      this.posthogService.capture('session_create_failed', {
        reason: 'missing_track',
      });
      return;
    }

    const trackName = this.getSelectedTrackName();
    const plannedStartIso =
      this.enableScheduleWindow && this.plannedStartTime
        ? new Date(this.plannedStartTime).toISOString()
        : undefined;
    const plannedEndIso =
      this.enableScheduleWindow && this.plannedEndTime
        ? new Date(this.plannedEndTime).toISOString()
        : undefined;

    if (plannedStartIso && plannedEndIso) {
      if (new Date(plannedEndIso) <= new Date(plannedStartIso)) {
        this.error = 'End date must be after start date';
        this.posthogService.capture('session_create_failed', {
          reason: 'invalid_schedule_window',
        });
        return;
      }
    }

    const normalizedMinIRating = this.restrictByIRating
      ? this.normalizeOptionalRating(this.minIRating)
      : undefined;
    const normalizedMaxIRating = this.restrictByIRating
      ? this.normalizeOptionalRating(this.maxIRating)
      : undefined;

    const staticWeatherConfig = this.enforceStaticWeather
      ? this.buildStaticWeatherConfig()
      : undefined;

    if (this.enforceStaticWeather && !staticWeatherConfig) {
      this.error = 'Complete all static weather fields before creating the session';
      this.posthogService.capture('session_create_failed', {
        reason: 'invalid_static_weather',
      });
      return;
    }

    if (
      normalizedMinIRating !== undefined &&
      normalizedMaxIRating !== undefined &&
      normalizedMinIRating > normalizedMaxIRating
    ) {
      this.error =
        'Minimum iRating must be less than or equal to maximum iRating';
      this.posthogService.capture('session_create_failed', {
        reason: 'invalid_irating_range',
      });
      return;
    }

    this.error = '';
    this.posthogService.capture('session_create_started', {
      visibility: this.visibility,
      hasScheduleWindow: !!plannedStartIso && !!plannedEndIso,
      lapsPerDriver: this.lapsPerDriver,
      allowSetupChanges: this.allowSetupChanges,
      enforceStaticWeather: this.enforceStaticWeather,
      selectedCarClassCount: this.selectedCarClasses.length,
      hasSeasonSelection: !!this.selectedSeasonId,
    });
    this.sessionService
      .startSession(
        trimmedSessionName,
        trackName,
        this.selectedTrackId,
        this.visibility === 'public',
        plannedStartIso,
        plannedEndIso,
        this.lapsPerDriver,
        this.selectedCarClasses.length > 0
          ? this.selectedCarClasses
          : undefined,
        this.allowSetupChanges,
        staticWeatherConfig,
        normalizedMinIRating,
        normalizedMaxIRating,
        this.selectedSeasonId,
      )
      .subscribe({
        next: (response) => {
          this.posthogService.capture('session_create_succeeded', {
            sessionId: response.session.id,
          });
          this.sessionReady = false;
          this.sessionName = '';
          this.visibility = this.getDefaultVisibility();
          this.selectedTrackId = null;
          this.enableScheduleWindow = false;
          this.plannedStartTime = '';
          this.plannedEndTime = '';
          this.lapsPerDriver = 10; // Reset to default
          this.allowSetupChanges = true;
          this.resetStaticWeatherConfig();
          this.restrictByIRating = false;
          this.minIRating = undefined;
          this.maxIRating = undefined;
          this.selectedCarClasses = []; // Reset car class selection
          this.selectedSeasonId = undefined; // Reset season selection
          this.sessionStarted.emit(response.session.id); // Emit created session id
        },
        error: (error) => {
          this.posthogService.capture('session_create_failed', {
            reason: error?.error?.error || 'request_failed',
          });
          this.error = error.error?.error || 'Failed to create session';
          this.sessionReady = false;
        },
      });
  }

  private normalizeOptionalRating(
    value: number | undefined,
  ): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0
      ? Math.floor(parsed)
      : undefined;
  }

  toggleIRatingRestrictions(): void {
    this.restrictByIRating = !this.restrictByIRating;
    if (!this.restrictByIRating) {
      this.minIRating = undefined;
      this.maxIRating = undefined;
    }
  }

  toggleScheduleWindow(): void {
    this.enableScheduleWindow = !this.enableScheduleWindow;
    if (!this.enableScheduleWindow) {
      this.plannedStartTime = '';
      this.plannedEndTime = '';
    }
  }

  toggleStaticWeatherEnforcement(): void {
    this.enforceStaticWeather = !this.enforceStaticWeather;
    if (this.enforceStaticWeather) {
      this.applyStaticWeatherConfig(DEFAULT_STATIC_WEATHER_CONFIG);
    } else {
      this.setDefaultStaticWeatherInputs();
    }
  }

  cancelPrepare(): void {
    this.sessionReady = false;
    this.error = '';
  }

  private buildStaticWeatherConfig(): StaticWeatherConfig | undefined {
    const airTempC = this.normalizeFiniteNumber(this.staticAirTempC);
    const relativeHumidityPct = this.normalizeFiniteNumber(
      this.staticRelativeHumidityPct,
    );
    const windSpeedMps = this.normalizeFiniteNumber(this.staticWindSpeedMps);

    if (
      airTempC === undefined ||
      relativeHumidityPct === undefined ||
      windSpeedMps === undefined
    ) {
      return undefined;
    }

    return {
      skies: this.staticWeatherSkies,
      trackMoisture: this.staticTrackMoisture,
      airTempC,
      relativeHumidityPct,
      windSpeedMps,
      windDirection: this.staticWindDirection,
    };
  }

  private normalizeFiniteNumber(value: number | undefined): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private resetStaticWeatherConfig(): void {
    this.enforceStaticWeather = false;
    this.setDefaultStaticWeatherInputs();
  }

  private setDefaultStaticWeatherInputs(): void {
    this.staticWeatherSkies = DEFAULT_STATIC_WEATHER_CONFIG.skies;
    this.staticTrackMoisture = DEFAULT_STATIC_WEATHER_CONFIG.trackMoisture;
    this.staticAirTempC = DEFAULT_STATIC_WEATHER_CONFIG.airTempC;
    this.staticRelativeHumidityPct =
      DEFAULT_STATIC_WEATHER_CONFIG.relativeHumidityPct;
    this.staticWindSpeedMps = DEFAULT_STATIC_WEATHER_CONFIG.windSpeedMps;
    this.staticWindDirection = DEFAULT_STATIC_WEATHER_CONFIG.windDirection;
  }

  private applyStaticWeatherConfig(config: StaticWeatherConfig): void {
    this.staticWeatherSkies = config.skies;
    this.staticTrackMoisture = config.trackMoisture;
    this.staticAirTempC = config.airTempC;
    this.staticRelativeHumidityPct = config.relativeHumidityPct;
    this.staticWindSpeedMps = config.windSpeedMps;
    this.staticWindDirection = config.windDirection;
  }

  formatTemperatureWithFahrenheit(value: number | undefined): string {
    if (value === undefined || value === null || !Number.isFinite(value)) {
      return 'C';
    }

    const fahrenheit = (value * 9) / 5 + 32;
    return `${value.toFixed(1)} C (${fahrenheit.toFixed(1)} F)`;
  }

  endSession(): void {
    this.posthogService.capture('session_end_started');
    const endedSessionId = this.activeSession?.id || null;
    this.sessionService.endSession().subscribe({
      next: () => {
        this.posthogService.capture('session_end_succeeded');
        this.activeSession = null;
        this.sessionReady = false;
        this.participants = [];
        if (endedSessionId) {
          this.router.navigate(['/session', endedSessionId]);
          return;
        }
        this.router.navigate(['/sessions']);
      },
      error: (error) => {
        this.posthogService.capture('session_end_failed', {
          reason: error?.error?.error || 'request_failed',
        });
        this.error = error.error?.error || 'Failed to end session';
      },
    });
  }

  // Session Sharing Methods

  loadParticipants(): void {
    if (!this.activeSession?.id) {
      return;
    }

    this.sessionService
      .getSessionParticipants(this.activeSession.id)
      .subscribe({
        next: (response) => {
          this.participants = response.participants || [];
        },
        error: (error) => {
          console.error('Failed to load participants:', error);
        },
      });
  }

  copySessionId(): void {
    if (!this.activeSession?.id) {
      return;
    }

    navigator.clipboard
      .writeText(this.activeSession.id)
      .then(() => {
        this.posthogService.capture('session_id_copied');
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      })
      .catch((err) => {
        this.posthogService.capture('session_id_copy_failed', {
          reason: err?.message || 'clipboard_failed',
        });
        console.error('Failed to copy session ID:', err);
        this.error = 'Failed to copy session ID to clipboard';
      });
  }

  getShareableLink(): string {
    if (!this.activeSession?.id) {
      return '';
    }
    return `${window.location.origin}?sessionId=${this.activeSession.id}`;
  }

  copyShareableLink(): void {
    const link = this.getShareableLink();
    if (!link) {
      return;
    }

    navigator.clipboard
      .writeText(link)
      .then(() => {
        this.posthogService.capture('session_share_link_copied');
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      })
      .catch((err) => {
        this.posthogService.capture('session_share_link_copy_failed', {
          reason: err?.message || 'clipboard_failed',
        });
        console.error('Failed to copy link:', err);
        this.error = 'Failed to copy link to clipboard';
      });
  }

  getParticipantTypeLabel(type: string): string {
    switch (type) {
      case 'owner':
        return 'Session Owner';
      case 'member':
        return 'Team Member';
      case 'remote':
        return 'Remote Driver';
      default:
        return 'Unknown';
    }
  }

  getParticipantTypeBadgeClass(type: string): string {
    switch (type) {
      case 'owner':
        return 'badge-owner';
      case 'member':
        return 'badge-owner';
      case 'remote':
        return 'badge-remote';
      default:
        return '';
    }
  }

  private getDefaultVisibility(): 'team' | 'public' {
    return this.hasTeamContext ? 'team' : 'public';
  }
}
